// src/model.ts
function createEmptyModel() {
  return {
    packages: [],
    // Core
    allPartDefs: [],
    allPartUsages: [],
    allActionDefs: [],
    allActionUsages: [],
    allPerforms: [],
    allFlows: [],
    allPortDefs: [],
    allItemDefs: [],
    allPorts: [],
    allProps: [],
    // State Machine
    allStateDefs: [],
    allStateUsages: [],
    allTransitions: [],
    // Requirements
    allRequirementDefs: [],
    allRequirementUsages: [],
    allSatisfies: [],
    // Constraints
    allConstraintDefs: [],
    allConstraintUsages: [],
    allAsserts: [],
    // Connections
    allConnectionDefs: [],
    allConnections: [],
    // Allocations
    allAllocationDefs: [],
    allAllocations: [],
    // Use Cases
    allUseCaseDefs: [],
    allUseCaseUsages: [],
    allActorUsages: [],
    allIncludeUseCases: [],
    // Interfaces
    allInterfaceDefs: [],
    allInterfaceUsages: [],
    // Verification
    allVerificationDefs: [],
    allVerificationUsages: [],
    // Analysis
    allAnalysisDefs: [],
    allAnalysisUsages: [],
    // Calculations
    allCalcDefs: [],
    allCalcUsages: [],
    // Viewpoints & Rendering
    allViewpointDefs: [],
    allViewpointUsages: [],
    allRenderingDefs: [],
    allRenderingUsages: [],
    // Metadata & Occurrences
    allMetadataDefs: [],
    allOccurrenceDefs: [],
    allOccurrenceUsages: [],
    // Other
    allEnums: [],
    allTypeAliases: [],
    allLets: [],
    allImports: [],
    allRelationships: [],
    allViews: [],
    allRules: [],
    allElements: []
  };
}
function createEmptyPackage(id, name, qualifiedName) {
  return {
    id,
    elementType: "package",
    name,
    qualifiedName,
    // Core definitions
    partDefs: [],
    partUsages: [],
    actionDefs: [],
    portDefs: [],
    itemDefs: [],
    // State Machine
    stateDefs: [],
    // Requirements & Constraints
    requirementDefs: [],
    constraintDefs: [],
    // Connections & Allocations
    connectionDefs: [],
    connections: [],
    allocationDefs: [],
    allocations: [],
    // Use Cases
    useCaseDefs: [],
    // Interfaces
    interfaceDefs: [],
    // Verification & Analysis
    verificationDefs: [],
    analysisDefs: [],
    // Calculations
    calcDefs: [],
    // Views, Viewpoints, Rendering
    viewDefs: [],
    viewpointDefs: [],
    renderingDefs: [],
    // Metadata & Occurrences
    metadataDefs: [],
    occurrenceDefs: [],
    // Satisfy & Assert
    satisfies: [],
    asserts: [],
    // Other
    relationships: [],
    views: [],
    rules: [],
    enums: [],
    typeAliases: [],
    lets: [],
    imports: [],
    packages: []
  };
}
function createDefaultDiagramState() {
  return {
    nodes: {},
    edges: {},
    viewport: { x: 0, y: 0, zoom: 1 },
    selectedNodeIds: [],
    selectedEdgeIds: []
  };
}
function createEmptyDocument(name) {
  const now = (/* @__PURE__ */ new Date()).toISOString();
  return {
    modelId: crypto.randomUUID(),
    name,
    sourceText: "",
    parsedModel: null,
    diagramState: createDefaultDiagramState(),
    version: 1,
    createdAt: now,
    updatedAt: now
  };
}

// src/ibd.ts
var DEFAULT_IBD_LAYOUT = {
  padding: 40,
  partSpacing: 30,
  defaultPartWidth: 140,
  defaultPartHeight: 80,
  portSize: 12
};

// src/tags.ts
var DEFAULT_TAG_DEFINITIONS = [
  // Lifecycle status tags
  { tag: "draft", label: "Draft", color: "yellow", icon: "FileEdit", description: "Work in progress, not ready for review" },
  { tag: "wip", label: "WIP", color: "yellow", icon: "Construction", description: "Actively being worked on" },
  { tag: "review", label: "Review", color: "orange", icon: "Eye", description: "Ready for review" },
  { tag: "active", label: "Active", color: "green", icon: "CheckCircle", description: "Active and in use" },
  { tag: "done", label: "Done", color: "green", icon: "CheckCircle2", description: "Completed" },
  { tag: "deprecated", label: "Deprecated", color: "red", icon: "AlertTriangle", description: "Deprecated, avoid using" },
  { tag: "experimental", label: "Experimental", color: "purple", icon: "FlaskConical", description: "Experimental, may change" },
  { tag: "speculative", label: "Speculative", color: "purple", icon: "Sparkles", description: "Speculative design, not committed" },
  // Domain tags (prefixed)
  { tag: "domain:vehicle", label: "Vehicle", color: "blue", icon: "Car", description: "Vehicle domain" },
  { tag: "domain:mission", label: "Mission", color: "blue", icon: "Target", description: "Mission planning domain" },
  { tag: "domain:platform", label: "Platform", color: "blue", icon: "Layers", description: "Platform infrastructure" },
  { tag: "domain:hardware", label: "Hardware", color: "cyan", icon: "Cpu", description: "Hardware components" },
  { tag: "domain:software", label: "Software", color: "cyan", icon: "Code", description: "Software components" },
  // Type/category tags
  { tag: "type:interface", label: "Interface", color: "gray", icon: "Plug", description: "Interface definitions" },
  { tag: "type:library", label: "Library", color: "gray", icon: "Library", description: "Reusable library" },
  { tag: "type:model", label: "Model", color: "gray", icon: "Box", description: "Domain model" },
  { tag: "type:view", label: "View", color: "gray", icon: "LayoutGrid", description: "Diagram view definitions" }
];
var DEFAULT_TAG_CONFIG = {
  definitions: DEFAULT_TAG_DEFINITIONS,
  defaultColor: "gray",
  showInheritedTags: true,
  maxVisibleTags: 3
};
function parseTag(raw) {
  const trimmed = raw.trim();
  const colonIdx = trimmed.indexOf(":");
  if (colonIdx > 0 && colonIdx < trimmed.length - 1) {
    return {
      raw: trimmed,
      namespace: trimmed.slice(0, colonIdx),
      value: trimmed.slice(colonIdx + 1)
    };
  }
  return { raw: trimmed, value: trimmed };
}
function extractMetaTags(sourceText) {
  if (!sourceText) return [];
  const tags = [];
  const tagsArrayPattern = /\btags\s*:\s*\[([^\]]*)\]/gi;
  let match;
  while ((match = tagsArrayPattern.exec(sourceText)) !== null) {
    const arrayContent = match[1];
    if (!arrayContent) continue;
    const tagPattern = /["']?([A-Za-z0-9_:-]+)["']?/g;
    let tagMatch;
    while ((tagMatch = tagPattern.exec(arrayContent)) !== null) {
      const tag = tagMatch[1].trim();
      if (tag && !tags.includes(tag)) {
        tags.push(tag);
      }
    }
  }
  return tags;
}
function getTagDefinition(tag, definitions = DEFAULT_TAG_DEFINITIONS, defaultColor = "gray") {
  const existing = definitions.find((d) => d.tag === tag);
  if (existing) return existing;
  const parsed = parseTag(tag);
  if (parsed.namespace) {
    const namespaceMatch = definitions.find((d) => {
      const pd = parseTag(d.tag);
      return pd.namespace === parsed.namespace;
    });
    if (namespaceMatch) {
      return {
        tag,
        label: parsed.value,
        color: namespaceMatch.color,
        icon: namespaceMatch.icon,
        description: `${parsed.namespace}: ${parsed.value}`
      };
    }
  }
  return {
    tag,
    label: tag,
    color: defaultColor,
    description: tag
  };
}
function getTagColorClasses(color) {
  switch (color) {
    case "gray":
      return { bg: "bg-gray-500/10", text: "text-gray-600 dark:text-gray-400", border: "border-gray-500/20" };
    case "blue":
      return { bg: "bg-blue-500/10", text: "text-blue-600 dark:text-blue-400", border: "border-blue-500/20" };
    case "green":
      return { bg: "bg-emerald-500/10", text: "text-emerald-600 dark:text-emerald-400", border: "border-emerald-500/20" };
    case "yellow":
      return { bg: "bg-amber-500/10", text: "text-amber-600 dark:text-amber-400", border: "border-amber-500/20" };
    case "orange":
      return { bg: "bg-orange-500/10", text: "text-orange-600 dark:text-orange-400", border: "border-orange-500/20" };
    case "red":
      return { bg: "bg-red-500/10", text: "text-red-600 dark:text-red-400", border: "border-red-500/20" };
    case "purple":
      return { bg: "bg-purple-500/10", text: "text-purple-600 dark:text-purple-400", border: "border-purple-500/20" };
    case "pink":
      return { bg: "bg-pink-500/10", text: "text-pink-600 dark:text-pink-400", border: "border-pink-500/20" };
    case "cyan":
      return { bg: "bg-cyan-500/10", text: "text-cyan-600 dark:text-cyan-400", border: "border-cyan-500/20" };
    default:
      return { bg: "bg-gray-500/10", text: "text-gray-600 dark:text-gray-400", border: "border-gray-500/20" };
  }
}
function mergeTags(localTags, inheritedTagSets) {
  const tagMap = /* @__PURE__ */ new Map();
  for (const { tags, source } of inheritedTagSets) {
    for (const raw of tags) {
      const existing = tagMap.get(raw);
      if (existing) {
        existing.sources.push(source);
      } else {
        tagMap.set(raw, { ...parseTag(raw), sources: [source] });
      }
    }
  }
  for (const raw of localTags) {
    const existing = tagMap.get(raw);
    if (existing) {
      const localSource = existing.sources.find((s) => s.depth === 0);
      if (!localSource) {
        existing.sources.unshift({
          docId: "",
          docName: "",
          inherited: false,
          depth: 0
        });
      }
    } else {
      tagMap.set(raw, {
        ...parseTag(raw),
        sources: [{ docId: "", docName: "", inherited: false, depth: 0 }]
      });
    }
  }
  return Array.from(tagMap.values());
}

// src/layout.ts
var MIN_PANE_SIZE_PCT = 15;
var DEFAULT_SPLIT_RATIO = [50, 50];
var MAX_PANES = 16;
var MAX_TABS_WARNING = 40;
export {
  DEFAULT_IBD_LAYOUT,
  DEFAULT_SPLIT_RATIO,
  DEFAULT_TAG_CONFIG,
  DEFAULT_TAG_DEFINITIONS,
  MAX_PANES,
  MAX_TABS_WARNING,
  MIN_PANE_SIZE_PCT,
  createDefaultDiagramState,
  createEmptyDocument,
  createEmptyModel,
  createEmptyPackage,
  extractMetaTags,
  getTagColorClasses,
  getTagDefinition,
  mergeTags,
  parseTag
};
//# sourceMappingURL=index.js.map